import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GroupsIcon from '@mui/icons-material/Groups';
import { Link as RouterLink } from 'react-router-dom';
import api from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import CreateGroupDialog from '@/components/groups/CreateGroupDialog';
import PageHeader from '@/components/Layout/PageHeader';
import { GROUP_ROLE_LABELS, type GroupMemberRole, type ShareGroup } from '@/types';

function groupInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || 'G';
}

function roleChipColor(role?: GroupMemberRole | null): 'secondary' | 'info' | 'default' {
  if (role === 'owner') return 'secondary';
  if (role === 'manager') return 'info';
  return 'default';
}

export default function GroupsPage() {
  const { user } = useAuth();
  const [groups, setGroups] = useState<ShareGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'developer';

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<{ groups: ShareGroup[] }>('/groups')
      .then((r) => setGroups(r.data.groups))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const managing = groups.filter((g) => g.my_role === 'owner' || g.my_role === 'manager').length;
    return { total: groups.length, managing };
  }, [groups]);

  return (
    <Box>
      <PageHeader
        icon={<GroupsIcon />}
        title="裁判群組"
        subtitle="管理分享影片、協作評核與群組成員"
        chips={
          !loading && groups.length > 0 ? (
            <>
              <Chip size="small" label={`${stats.total} 個群組`} color="secondary" variant="outlined" />
              {stats.managing > 0 && <Chip size="small" label={`${stats.managing} 個可管理`} variant="outlined" />}
            </>
          ) : undefined
        }
        actions={
          isAdmin ? (
            <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              建立群組
            </Button>
          ) : undefined
        }
      />

      {loading ? (
        <Grid container spacing={2}>
          {[0, 1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : groups.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            py: 6,
            textAlign: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 1.5, bgcolor: 'action.selected' }}>
            <GroupsIcon color="action" />
          </Avatar>
          <Typography fontWeight={700} gutterBottom>
            您尚未加入任何群組
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            群組管理員建立群組並加入您後，分享的分析影片會顯示於此
          </Typography>
          {isAdmin && (
            <Button variant="outlined" color="secondary" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              建立第一個群組
            </Button>
          )}
        </Card>
      ) : (
        <Grid container spacing={2}>
          {groups.map((g) => (
            <Grid item xs={12} md={6} key={g.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.2s',
                  '&:hover': {
                    boxShadow: 4,
                    borderColor: 'secondary.main',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardActionArea component={RouterLink} to={`/groups/${g.id}`} sx={{ height: '100%', alignItems: 'stretch' }}>
                  <CardContent sx={{ height: '100%' }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: 'secondary.main',
                            fontWeight: 800,
                            fontSize: '1.1rem',
                          }}
                        >
                          {groupInitial(g.name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography fontWeight={800} noWrap>
                            {g.name}
                          </Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                            {g.my_role && (
                              <Chip
                                size="small"
                                label={GROUP_ROLE_LABELS[g.my_role]}
                                color={roleChipColor(g.my_role)}
                                sx={{ height: 22 }}
                              />
                            )}
                            <Chip size="small" variant="outlined" label={`${g.member_count ?? 0} 位成員`} sx={{ height: 22 }} />
                          </Stack>
                          {g.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {g.description}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      <ChevronRightIcon color="action" sx={{ mt: 0.5, flexShrink: 0 }} />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <CreateGroupDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
    </Box>
  );
}
